import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ROLE_LABEL } from "@/lib/constants";
import { Megaphone, Send, Bell, BellRing, Mail, MessageCircle, Users, Building2, AlertTriangle, ExternalLink, Copy } from "lucide-react";

const ROLE_OPTIONS = [
  "KETUA_KLASIS","WAKIL_KETUA","SEKRETARIS_KLASIS","WAKIL_SEKRETARIS","BENDAHARA_KLASIS","WAKIL_BENDAHARA",
  "KOORDINATOR_BIDANG","BP2K","GEMBALA_JEMAAT","SEKRETARIS_JEMAAT","BENDAHARA_JEMAAT","ADMIN_KLASIS","PANITIA","VIEWER_INTERNAL"
];

export default function BroadcastPengumuman() {
  const { user } = useAuth();
  const [churches, setChurches] = useState([]);
  const [form, setForm] = useState({
    title: "", body: "", priority: "NORMAL",
  });
  const [channels, setChannels] = useState({ IN_APP: true, EMAIL: false, WA_GROUP: false, PUSH: false });
  const [targetMode, setTargetMode] = useState("ALL");
  const [targetRoles, setTargetRoles] = useState([]);
  const [targetChurchIds, setTargetChurchIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const canSend = ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS","KETUA_KLASIS","BP2K"].includes(user?.role);

  useEffect(() => {
    api.get("/churches").then((r) => setChurches(r.data.filter((c) => c.type === "JEMAAT")));
  }, []);

  const buildPayload = () => ({
    title: form.title,
    body: form.body,
    priority: form.priority,
    channels: Object.keys(channels).filter((k) => channels[k]),
    target_mode: targetMode,
    target_roles: (targetMode === "BY_ROLE" || targetMode === "MULTI") ? targetRoles : null,
    target_church_ids: (targetMode === "BY_CHURCH" || targetMode === "MULTI") ? targetChurchIds : null,
  });

  const doPreview = async () => {
    if (targetMode === "BY_ROLE" && targetRoles.length === 0) { toast.error("Pilih minimal 1 role"); return; }
    if (targetMode === "BY_CHURCH" && targetChurchIds.length === 0) { toast.error("Pilih minimal 1 jemaat"); return; }
    try {
      const { data } = await api.post("/announcements/preview-targets", { ...buildPayload(), title: form.title || "preview", body: form.body || "preview" });
      setPreview(data);
      toast.success(`${data.count} penerima cocok`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Judul & isi pengumuman wajib"); return; }
    const activeChannels = Object.keys(channels).filter((k) => channels[k]);
    if (activeChannels.length === 0) { toast.error("Pilih minimal 1 kanal pengiriman"); return; }
    setSending(true); setResult(null);
    try {
      const { data } = await api.post("/announcements/broadcast", buildPayload());
      setResult(data);
      toast.success(`Pengumuman dikirim ke ${data.target_count} penerima`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const openWa = () => {
    if (result?.wa_group_link) {
      window.open(result.wa_group_link, "_blank");
    }
  };

  const toggleRole = (r) => setTargetRoles((prev) => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  const toggleChurch = (id) => setTargetChurchIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  if (!canSend) {
    return (
      <AppShell>
        <div className="p-12 text-center border border-dashed border-border rounded-lg">
          <Megaphone className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm text-muted-foreground">Hanya Sekretariat/Admin/Ketua/BP2K yang dapat broadcast pengumuman.</div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Komunikasi • Broadcast</div>
        <h1 className="font-heading font-bold text-3xl mt-1 flex items-center gap-3">
          <Megaphone className="h-7 w-7 text-secondary" /> Broadcast Pengumuman
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Kirim pengumuman ke seluruh Klasis, per role, atau per jemaat via Notifikasi App, Email, dan Group WhatsApp BPK Mimika.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Content */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Isi Pengumuman</CardTitle></CardHeader>
            <CardContent className="p-6 pt-0 space-y-3">
              <div>
                <Label className="text-xs">Judul</Label>
                <Input value={form.title} onChange={(e)=>setForm({...form, title:e.target.value})} placeholder="Rapat Koordinasi Bidang…" data-testid="input-ann-title" />
              </div>
              <div>
                <Label className="text-xs">Isi Pengumuman</Label>
                <Textarea rows={6} value={form.body} onChange={(e)=>setForm({...form, body:e.target.value})} placeholder="Sampaikan detail pengumuman: waktu, tempat, agenda, dan kontak…" data-testid="input-ann-body" />
              </div>
              <div>
                <Label className="text-xs">Prioritas</Label>
                <div className="flex gap-2 mt-1">
                  {[
                    {k: "NORMAL", label: "Normal"},
                    {k: "HIGH", label: "Penting"},
                    {k: "URGENT", label: "Mendesak"},
                  ].map((p) => (
                    <button
                      key={p.k}
                      type="button"
                      onClick={() => setForm({...form, priority: p.k})}
                      className={`flex-1 py-2 rounded-md text-sm border transition ${form.priority === p.k ? "border-primary bg-primary/10 text-primary font-semibold" : "border-border hover:border-primary/50"}`}
                      data-testid={`btn-priority-${p.k.toLowerCase()}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Channels */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Kanal Pengiriman</CardTitle></CardHeader>
            <CardContent className="p-6 pt-0 space-y-2">
              {[
                {k: "IN_APP", label: "Notifikasi Aplikasi", desc: "Muncul di lonceng notifikasi user", icon: Bell},
                {k: "PUSH", label: "Push ke HP/Desktop", desc: "Notifikasi sistem ke perangkat yang berlangganan", icon: BellRing},
                {k: "EMAIL", label: "Email", desc: "Kirim email HTML ke setiap penerima via Resend", icon: Mail},
                {k: "WA_GROUP", label: "Group WhatsApp BPK Mimika", desc: "Generate link berisi teks — Anda paste ke Group secara manual", icon: MessageCircle},
              ].map((c) => {
                const Ico = c.icon;
                return (
                  <label key={c.k} className={`flex items-center gap-3 p-3 rounded-md border cursor-pointer transition ${channels[c.k] ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"}`} data-testid={`label-channel-${c.k.toLowerCase()}`}>
                    <input type="checkbox" checked={channels[c.k]} onChange={(e)=>setChannels({...channels, [c.k]:e.target.checked})} data-testid={`checkbox-channel-${c.k.toLowerCase()}`} />
                    <Ico className={`h-4 w-4 ${channels[c.k] ? "text-primary" : "text-muted-foreground"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{c.label}</div>
                      <div className="text-[0.7rem] text-muted-foreground">{c.desc}</div>
                    </div>
                  </label>
                );
              })}
            </CardContent>
          </Card>

          {/* Target */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Target Penerima</CardTitle></CardHeader>
            <CardContent className="p-6 pt-0 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  {k: "ALL", label: "Semua User", icon: Users},
                  {k: "BY_ROLE", label: "Per Role", icon: Users},
                  {k: "BY_CHURCH", label: "Per Jemaat", icon: Building2},
                  {k: "MULTI", label: "Kombinasi", icon: Users},
                ].map((t) => {
                  const Ico = t.icon;
                  return (
                    <button key={t.k} onClick={()=>setTargetMode(t.k)} className={`p-3 rounded-md border text-sm transition ${targetMode === t.k ? "border-primary bg-primary/10 text-primary font-semibold" : "border-border hover:border-primary/50"}`} data-testid={`btn-target-${t.k.toLowerCase()}`}>
                      <Ico className="h-4 w-4 mb-1 mx-auto" />
                      {t.label}
                    </button>
                  );
                })}
              </div>

              {(targetMode === "BY_ROLE" || targetMode === "MULTI") && (
                <div>
                  <Label className="text-xs">Role ({targetRoles.length} dipilih)</Label>
                  <div className="grid grid-cols-2 gap-1.5 mt-1.5 max-h-48 overflow-y-auto p-2 border border-border rounded-md">
                    {ROLE_OPTIONS.map((r) => (
                      <label key={r} className="flex items-center gap-2 text-xs cursor-pointer p-1 hover:bg-muted/50 rounded">
                        <input type="checkbox" checked={targetRoles.includes(r)} onChange={()=>toggleRole(r)} data-testid={`checkbox-role-${r}`} />
                        {ROLE_LABEL[r] || r}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {(targetMode === "BY_CHURCH" || targetMode === "MULTI") && (
                <div>
                  <Label className="text-xs">Jemaat ({targetChurchIds.length} dipilih)</Label>
                  <div className="grid grid-cols-2 gap-1.5 mt-1.5 p-2 border border-border rounded-md">
                    {churches.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer p-1 hover:bg-muted/50 rounded">
                        <input type="checkbox" checked={targetChurchIds.includes(c.id)} onChange={()=>toggleChurch(c.id)} data-testid={`checkbox-church-${c.code}`} />
                        {c.short_name || c.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <Button size="sm" variant="outline" onClick={doPreview} className="rounded-full" data-testid="btn-preview-targets">
                Preview Target
              </Button>

              {preview && (
                <div className="bg-primary/5 border border-primary/20 rounded-md p-3 text-xs">
                  <div className="font-semibold text-primary mb-1">{preview.count} penerima akan menerima pengumuman ini.</div>
                  {preview.count > 0 && (
                    <div className="text-muted-foreground">Contoh: {preview.sample.map(s => s.name).join(", ")}{preview.count > preview.sample.length ? ", …" : ""}</div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card className="border-primary/40 bg-primary/5">
            <CardContent className="p-6">
              <div className="text-[0.65rem] tracking-[0.18em] uppercase text-primary font-semibold">Siap Kirim</div>
              <div className="font-heading font-bold text-4xl mt-1 text-primary">
                {preview?.count ?? "?"}
              </div>
              <div className="text-xs text-muted-foreground mt-1">penerima broadcast</div>
              <div className="flex flex-wrap gap-1 mt-3">
                {Object.entries(channels).filter(([_, v]) => v).map(([k]) => (
                  <StatusBadge key={k} status="info" label={k === "IN_APP" ? "App" : k === "PUSH" ? "Push" : k === "EMAIL" ? "Email" : "WA"} />
                ))}
              </div>
              <Button
                className="w-full rounded-full mt-4"
                onClick={send}
                disabled={sending || !form.title.trim() || !form.body.trim()}
                data-testid="btn-send-broadcast"
              >
                <Send className="h-4 w-4 mr-2" /> {sending ? "Mengirim…" : "Kirim Broadcast"}
              </Button>
            </CardContent>
          </Card>

          {result && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-xs">Hasil Broadcast</CardTitle></CardHeader>
              <CardContent className="p-4 pt-0 text-xs space-y-2">
                <div>Target: <strong>{result.target_count}</strong> user</div>
                {result.channels.includes("EMAIL") && (
                  <div className={result.email_failed > 0 ? "text-amber-800" : "text-emerald-700"}>
                    Email: {result.email_sent} terkirim {result.email_failed > 0 && <span>({result.email_failed} gagal)</span>}
                  </div>
                )}
                {result.channels.includes("IN_APP") && (
                  <div className="text-emerald-700">In-App: tersimpan, muncul di lonceng notifikasi</div>
                )}
                {result.channels.includes("PUSH") && (
                  <div className={(result.push_failed || 0) > 0 ? "text-amber-800" : "text-emerald-700"}>
                    Push: {result.push_sent || 0} terkirim{(result.push_failed || 0) > 0 && <span> ({result.push_failed} gagal)</span>}
                  </div>
                )}
                {result.wa_group_link && (
                  <div className="pt-2 border-t border-border">
                    <div className="mb-2 text-muted-foreground">Untuk Group WA — klik link:</div>
                    <Button size="sm" onClick={openWa} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-open-wa-broadcast">
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> Buka WhatsApp & Paste ke Group
                    </Button>
                    <button onClick={()=>{navigator.clipboard?.writeText(result.wa_group_link); toast.success("Link disalin");}} className="w-full text-[0.65rem] text-primary hover:underline mt-1.5 flex items-center gap-1 justify-center" data-testid="btn-copy-wa-link">
                      <Copy className="h-2.5 w-2.5" /> Salin link
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-4 text-[0.7rem] text-muted-foreground space-y-1">
              <div className="font-semibold text-foreground text-xs">Tips</div>
              <div>• Preview target sebelum kirim untuk validasi jumlah penerima</div>
              <div>• Email butuh alamat email di profil user — user tanpa email dilewati</div>
              <div>• Group WA butuh 1 klik manual paste (kebijakan Meta)</div>
              <div>• Semua broadcast tercatat di Audit Log</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
